import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiDownload, FiImage, FiPlus, FiRefreshCw, FiSave, FiTrash2, FiZap } from 'react-icons/fi';
import { downloadStoryImage } from '../../utils/downloadStoryImage';
import InstagramManualPostComposer from './InstagramManualPostComposer';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { normalizeSchedule } from '../../utils/marketingScheduleForward';
import { instagramPublishPlan, publishPlanLabel, publishPlanShort } from '../../utils/instagramPublishPlan';
import '../../components/Button.css';

/** Sorteersleutel: geplande datum+tijd, anders publicatietijdstip. */
function instagramPlanningSortKey(post) {
  if (post.instagram_posted_at) {
    const t = Date.parse(post.instagram_posted_at);
    if (!Number.isNaN(t)) return t;
  }
  const day = String(post.starts_on || '').slice(0, 10);
  if (!day) return 0;
  const time = String(post.instagram_publish_time || '00:00').slice(0, 5);
  const t = Date.parse(`${day}T${time}:00`);
  return Number.isNaN(t) ? Date.parse(day) : t;
}

function compareInstagramPlanning(a, b, filter = 'all') {
  if (filter === 'published') {
    const ta = Date.parse(a.instagram_posted_at || '') || 0;
    const tb = Date.parse(b.instagram_posted_at || '') || 0;
    if (tb !== ta) return tb - ta;
    return (b.id || 0) - (a.id || 0);
  }
  const ka = instagramPlanningSortKey(a);
  const kb = instagramPlanningSortKey(b);
  if (ka !== kb) return ka - kb;
  return (a.id || 0) - (b.id || 0);
}

function captionForEditing(post) {
  const meta = String(post.meta_primary_text || '').trim();
  if (meta) return meta;
  return String(post.caption || '').trim();
}

function parsePostCopy(post) {
  const raw = post?.ai_copy_json;
  if (raw && typeof raw === 'object') return raw;
  return {};
}

/** Story op Instagram: live, handmatig klaar, mislukt, nog niet, of n.v.t. */
function storyLiveState(post, publishPlan) {
  if (!publishPlan?.story) return 'na';
  if (post.instagram_story_post_id) return 'live';
  if (parsePostCopy(post).instagram_story_manual_ready) return 'manual';
  if (post.instagram_story_publish_error) return 'failed';
  if (post.instagram_post_id || post.preview_state === 'published') return 'missing';
  return 'pending';
}

function storyPlanBadgeTitle(post, publishPlan, storyState) {
  if (!publishPlan?.story || storyState === 'na' || storyState === 'pending') return undefined;
  if (storyState === 'live') return 'Feed en story staan op Instagram.';
  if (storyState === 'manual') return 'Feed staat online. Story plaats je handmatig in Instagram (link-sticker).';
  if (storyState === 'failed' || storyState === 'missing') {
    return post.instagram_story_publish_error || 'Feed staat online; story niet geplaatst.';
  }
  return undefined;
}

function InstagramPreviewCard({
  post,
  accountLabel,
  defaultPublishTime,
  igAutoEnabled,
  igStoryManualLink,
  igVbpDefaults,
  onPostUpdated,
  onPostDeleted,
}) {
  const handle = accountLabel?.replace(/\s+/g, '').toLowerCase() || '4shine';
  const isPublished = post.preview_state === 'published';
  const publishPlan = useMemo(
    () => instagramPublishPlan(post, igVbpDefaults || {}),
    [post, igVbpDefaults]
  );
  const planLabel = publishPlanLabel(publishPlan);
  const storyState = useMemo(() => storyLiveState(post, publishPlan), [post, publishPlan]);
  const storyPlanTitle = useMemo(
    () => storyPlanBadgeTitle(post, publishPlan, storyState),
    [post, publishPlan, storyState]
  );
  const storyManualWorkflow = igStoryManualLink !== false;
  const [imageUrl, setImageUrl] = useState(post.instagram_image_url || '');
  const [storyImageUrl, setStoryImageUrl] = useState(post.instagram_story_image_url || '');
  const [storyLinkUrl, setStoryLinkUrl] = useState(post.instagram_story_link_url || '');
  const [caption, setCaption] = useState(() => captionForEditing(post));
  const [aiGenerating, setAiGenerating] = useState(false);
  const triedAutoAi = useRef(false);
  const triedRestorePlainSfeer = useRef(false);
  const [tileError, setTileError] = useState('');
  const [startsOn, setStartsOn] = useState(post.starts_on || '');
  const [publishTime, setPublishTime] = useState(post.instagram_publish_time || '');
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedHint, setSavedHint] = useState('');

  const storyDownloadName = `4shine-story-${post.id || 'export'}.jpg`;

  useEffect(() => {
    setImageUrl(post.instagram_image_url || '');
    setStoryImageUrl(post.instagram_story_image_url || '');
    setStoryLinkUrl(post.instagram_story_link_url || '');
    setCaption(captionForEditing(post));
    setStartsOn(post.starts_on || '');
    setPublishTime(post.instagram_publish_time || '');
  }, [
    post.id,
    post.instagram_image_url,
    post.instagram_story_image_url,
    post.instagram_story_link_url,
    post.caption,
    post.meta_primary_text,
    post.starts_on,
    post.instagram_publish_time,
  ]);

  useEffect(() => {
    triedAutoAi.current = false;
    triedRestorePlainSfeer.current = false;
    setTileError('');
  }, [post.id]);

  const hasBrandedTileUrl = (url) =>
    String(url || '').includes('/deal-images/') || /\/deal-\d+-[a-f0-9]+\.jpg/i.test(String(url || ''));

  /** Dagdeal-opmaak hoort alleen op story — feed/sfeer-URL blijft de ruwe shopfoto. */
  useEffect(() => {
    if (isPublished || !post.is_vbp_deal || !imageUrl || !hasBrandedTileUrl(imageUrl)) return;
    if (triedRestorePlainSfeer.current) return;
    triedRestorePlainSfeer.current = true;
    (async () => {
      setBusy(true);
      try {
        const res = await api.post(`/marketing/events/${post.id}/resolve-sfeer-image`, { branded: false });
        const raw = res.data?.image_url || '';
        if (raw && !hasBrandedTileUrl(raw)) {
          setImageUrl(raw);
          onPostUpdated(post.id, { instagram_image_url: raw });
          setTileError('');
        }
      } catch (e) {
        setTileError(e.response?.data?.error || e.message || 'Sfeerfoto herstellen mislukt');
      } finally {
        setBusy(false);
      }
    })();
  }, [post.id, post.is_vbp_deal, imageUrl, isPublished, onPostUpdated]);

  const flashSaved = () => {
    setSavedHint('Opgeslagen');
    window.setTimeout(() => setSavedHint(''), 2000);
  };

  const savePublishPlan = async (nextPlan) => {
    const body = {
      instagram_publish_feed: nextPlan.feed,
      instagram_story_auto_publish: nextPlan.story,
    };
    setBusy(true);
    try {
      await api.patch(`/marketing/events/${post.id}`, body);
      const nextCopy = { ...(post.ai_copy_json || {}), instagram_publish_feed: nextPlan.feed };
      onPostUpdated(post.id, {
        instagram_story_auto_publish: nextPlan.story,
        ai_copy_json: nextCopy,
        instagram_publish_feed: nextPlan.feed,
      });
      flashSaved();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const patchPost = async (body) => {
    setBusy(true);
    try {
      await api.patch(`/marketing/events/${post.id}`, body);
      onPostUpdated(post.id, {
        ...body,
        ...(body.instagram_image_url !== undefined ? { instagram_image_url: body.instagram_image_url } : {}),
        ...(body.ai_copy_json
          ? {
              ai_copy_json: body.ai_copy_json,
              meta_primary_text: body.ai_copy_json.meta_primary_text || '',
              has_ai_caption: String(body.ai_copy_json.meta_primary_text || '').trim().length >= 15,
              caption: String(body.ai_copy_json.meta_primary_text || '').trim() || post.caption,
            }
          : {}),
        ...(body.starts_on !== undefined ? { starts_on: body.starts_on } : {}),
        ...(body.instagram_publish_time !== undefined
          ? { instagram_publish_time: body.instagram_publish_time }
          : {}),
        ...(body.instagram_story_link_url !== undefined
          ? { instagram_story_link_url: body.instagram_story_link_url }
          : {}),
      });
      flashSaved();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const loadGallery = async () => {
    if (!post.has_product_gallery) return;
    setGalleryLoading(true);
    try {
      const res = await api.get(`/marketing/events/${post.id}/gallery-images`);
      setGallery(res.data?.images || []);
      setGalleryOpen(true);
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setGalleryLoading(false);
    }
  };

  const selectGalleryImage = async (img) => {
    if (!img?.url || img.url === imageUrl) return;
    setImageUrl(img.url);
    await patchPost({ instagram_image_url: img.url });
    setGallery((list) => list.map((g) => ({ ...g, selected: g.url === img.url })));
  };

  const autoSfeer = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/marketing/events/${post.id}/resolve-sfeer-image`, {
        branded: false,
      });
      const url = res.data?.image_url || '';
      setImageUrl(url);
      onPostUpdated(post.id, { instagram_image_url: url });
      if (galleryOpen) {
        setGallery((list) => list.map((g) => ({ ...g, selected: g.url === url })));
      }
      flashSaved();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const renderStoryTile = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/marketing/events/${post.id}/render-story-image`, {
        background_url: imageUrl && !hasBrandedTileUrl(imageUrl) ? imageUrl : undefined,
      });
      const raw = res.data?.image_url || '';
      const url = raw ? `${raw}${raw.includes('?') ? '&' : '?'}v=${Date.now()}` : '';
      setStoryImageUrl(url);
      onPostUpdated(post.id, { instagram_story_image_url: raw, instagram_story_auto_publish: true });
      flashSaved();
      alert('Story-tegel (9:16) opgeslagen — wordt mee gepost bij publiceren.');
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const generateAiCaption = async () => {
    setAiGenerating(true);
    try {
      const res = await api.post(`/marketing/events/${post.id}/ai-copy`);
      const copy = res.data?.copy || {};
      const text = String(copy.meta_primary_text || '').trim();
      setCaption(text || captionForEditing({ ...post, caption: post.caption, meta_primary_text: text }));
      onPostUpdated(post.id, {
        ai_copy_json: copy,
        meta_primary_text: text,
        has_ai_caption: text.length >= 15,
        caption: text || post.caption,
      });
      flashSaved();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setAiGenerating(false);
    }
  };

  useEffect(() => {
    if (isPublished || triedAutoAi.current) return;
    if (!publishPlan.feed) return;
    if (post.has_ai_caption) return;
    if (post.preview_state !== 'scheduled' && !post.instagram_auto_publish) return;
    triedAutoAi.current = true;
    generateAiCaption();
  }, [
    post.id,
    post.has_ai_caption,
    post.preview_state,
    post.instagram_auto_publish,
    isPublished,
    publishPlan.feed,
  ]);

  const saveCaption = async () => {
    const nextCopy = { ...(post.ai_copy_json || {}), meta_primary_text: caption.trim() };
    await patchPost({ ai_copy_json: nextCopy });
  };

  const applyPublishResponse = (res) => {
    const warn = res.data?.shopping_tag_warning;
    const feedErr = res.data?.feed_error;
    const storyOk = res.data?.story_media_id;
    const storyErr = res.data?.story_error;
    const feedOk = Boolean(res.data?.media_id);
    const storyManualReady = Boolean(res.data?.story_manual_ready);
    const anyLive = feedOk || storyOk || storyManualReady || post.instagram_post_id;
    const nextCopy = storyManualReady
      ? { ...(post.ai_copy_json || {}), instagram_story_manual_ready: true }
      : post.ai_copy_json;
    onPostUpdated(post.id, {
      instagram_post_id: res.data?.media_id || post.instagram_post_id,
      instagram_story_post_id: storyOk || post.instagram_story_post_id,
      instagram_story_posted_at: storyOk ? new Date().toISOString() : post.instagram_story_posted_at,
      preview_state: anyLive ? 'published' : post.preview_state,
      instagram_publish_error: feedErr || (feedOk ? null : post.instagram_publish_error),
      instagram_story_publish_error: storyErr || (storyOk ? null : post.instagram_story_publish_error),
      ai_copy_json: nextCopy,
    });
    let msg = '';
    if (feedOk) msg = 'Feed gepubliceerd.';
    else if (publishPlan.story && storyOk) msg = 'Story gepubliceerd.';
    else if (storyManualReady) msg = 'Story-tegel klaar.';
    else msg = 'Klaar.';
    if (feedOk && storyOk) msg += ' Story ook live.';
    else if (feedErr && storyOk) msg += '\n\nFeed mislukt, story wél live.';
    else if (storyErr) msg += `\n\nStory mislukt: ${storyErr}`;
    if (storyManualReady) msg += ' Story: handmatig plaatsen in de app.';
    if (res.data?.story_link_warning) msg += `\n\n${res.data.story_link_warning}`;
    if (feedErr) msg += `\n\nFeed: ${feedErr}`;
    if (warn) msg += `\n\nLet op: ${warn}`;
    alert(msg);
    window.dispatchEvent(new Event('marketing-stats-refresh'));
    window.dispatchEvent(new Event('marketing-ig-feed-refresh'));
  };

  const publishNow = async () => {
    const needsFeedImage = publishPlan.feed;
    const needsStoryAsset = publishPlan.story;
    if (needsFeedImage && !imageUrl && !post.has_product_gallery) {
      alert('Eerst een afbeelding kiezen (Auto sfeer of Foto uit shop).');
      return;
    }
    if (needsStoryAsset && !needsFeedImage && !imageUrl && !storyImageUrl && !post.has_product_gallery) {
      alert('Voor een story: kies een sfeerfoto of genereer een story-tegel (9:16).');
      return;
    }
    const ok = window.confirm('Deze post nu direct naar Instagram sturen?');
    if (!ok) return;
    setBusy(true);
    try {
      const savedImage = String(post.instagram_image_url || '').trim();
      const draftImage = String(imageUrl || '').trim();
      const res = await api.post(`/marketing/events/${post.id}/publish-instagram`, {
        image_url: draftImage && draftImage !== savedImage ? draftImage : undefined,
        caption: caption.trim() || undefined,
      });
      applyPublishResponse(res);
    } catch (e) {
      alert(e.response?.data?.error || e.message);
      window.dispatchEvent(new Event('marketing-ig-feed-refresh'));
    } finally {
      setBusy(false);
    }
  };

  const publishStoryRetry = async () => {
    if (!publishPlan.story) return;
    if (!storyImageUrl && !post.has_product_gallery) {
      alert('Geen story-tegel — genereer eerst Story-tegel of kies een sfeerfoto.');
      return;
    }
    const ok = window.confirm(
      storyManualWorkflow
        ? 'Story opnieuw voorbereiden? (Automatisch online zetten kan alleen als je in Instellingen “Story handmatig met link-sticker” uitzet.)'
        : 'Story nu via Instagram API plaatsen?'
    );
    if (!ok) return;
    setBusy(true);
    try {
      const res = await api.post(`/marketing/events/${post.id}/publish-instagram`, {
        caption: caption.trim() || undefined,
      });
      applyPublishResponse(res);
    } catch (e) {
      alert(e.response?.data?.error || e.message);
      window.dispatchEvent(new Event('marketing-ig-feed-refresh'));
    } finally {
      setBusy(false);
    }
  };

  const saveSchedule = async () => {
    const schedule = normalizeSchedule(startsOn, publishTime, defaultPublishTime || '10:00');
    await patchPost({
      starts_on: schedule.startsOn,
      instagram_publish_time: schedule.publishTime,
    });
  };

  const removePost = async () => {
    const isLive = post.preview_state === 'published';
    const ok = window.confirm(
      isLive
        ? 'Dit item uit het Instagram-overzicht verwijderen?\n\nDe post blijft gewoon op Instagram staan — alleen de tegel hier verdwijnt.'
        : 'Deze post uit het overzicht en de marketingkalender verwijderen?'
    );
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(`/marketing/events/${post.id}`);
      onPostDeleted(post.id);
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const instagramPreviewCaption = useMemo(() => {
    const savedMeta = String(post.meta_primary_text || '').trim();
    const editing = String(caption || '').trim();
    if (post.caption && (!editing || editing === savedMeta)) {
      return post.caption;
    }
    return editing || post.caption || '';
  }, [caption, post.caption, post.meta_primary_text]);

  return (
    <article className={`ig-preview-card ig-preview-${post.preview_state}`}>
      <header className="ig-preview-header">
        <div className="ig-preview-avatar" aria-hidden />
        <div>
          <strong className="ig-preview-handle">{handle}</strong>
          <span className="ig-preview-meta">
            {post.preview_state === 'published' && post.instagram_posted_at
              ? new Date(post.instagram_posted_at).toLocaleString('nl-NL', {
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : startsOn
                ? `Gepland ${startsOn}${publishTime ? ` ${publishTime}` : ''}`
                : 'Concept'}
          </span>
        </div>
        <span className={`ig-preview-badge ig-preview-badge-${post.preview_state}`}>
          {post.preview_state === 'published' ? 'Live' : post.preview_state === 'scheduled' ? 'Gepland' : 'Concept'}
        </span>
        <span
          className={`ig-preview-badge ig-preview-badge-plan${storyState === 'manual' || storyState === 'failed' || storyState === 'missing' ? ' ig-preview-badge-plan-story-pending' : ''}`}
          title={storyPlanTitle || 'Geplande kanalen'}
        >
          {planLabel}
        </span>
      </header>

      {tileError ? (
        <p className="ig-preview-tile-error muted" role="status">
          Dagdeal-opmaak: {tileError}
        </p>
      ) : null}

      <div className="ig-preview-media ig-preview-media-dual">
        {publishPlan.feed ? (
          imageUrl ? (
            <figure className="ig-preview-media-figure">
              <img src={imageUrl} alt="" loading="lazy" className="ig-preview-feed-img" />
              <figcaption>Feed</figcaption>
            </figure>
          ) : (
            <div className="ig-preview-media-placeholder">Geen feed-afbeelding — kies uit gallery of auto sfeer</div>
          )
        ) : (
          <div className="ig-preview-media-placeholder ig-preview-media-skipped">Geen feed — alleen story</div>
        )}
        {publishPlan.story ? (
          storyImageUrl ? (
            <figure className="ig-preview-media-figure ig-preview-media-figure-story">
              <img src={storyImageUrl} alt="" loading="lazy" className="ig-preview-story-img" title="Story 9:16" />
              <figcaption className="ig-preview-story-caption">
                <span>
                  Story 9:16
                  {isPublished && storyState === 'manual' ? (
                    <span className="ig-preview-story-caption-hint" title="Nog niet op Instagram — handmatig plaatsen">
                      {' '}
                      · handmatig
                    </span>
                  ) : null}
                </span>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm ig-preview-story-download"
                  disabled={busy}
                  onClick={() => downloadStoryImage(storyImageUrl, storyDownloadName)}
                >
                  <FiDownload /> Download
                </button>
              </figcaption>
            </figure>
          ) : (
            <div className="ig-preview-media-placeholder ig-preview-story-placeholder">
              Story-tegel nog niet gemaakt — wordt bij publiceren gegenereerd
            </div>
          )
        ) : null}
      </div>
      {!isPublished && publishPlan.story && (
        <>
          <p className="muted ig-preview-story-note">
            {publishPlan.feed
              ? 'Story wordt mee gepost op het geplande moment (na feed).'
              : 'Alleen story — geen feed-post.'}
          </p>
          <div className="ig-preview-image-url-block">
            <label className="ig-preview-label">
              Story-link (link-sticker)
              <input
                className="form-input"
                type="url"
                value={storyLinkUrl}
                disabled={busy}
                placeholder="https://www.4shine.nl/… (productpagina of shop)"
                onChange={(e) => setStoryLinkUrl(e.target.value)}
              />
            </label>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={busy}
              onClick={() =>
                patchPost({
                  instagram_story_link_url: storyLinkUrl.trim() || null,
                })
              }
            >
              <FiSave /> Story-link opslaan
            </button>
          </div>
        </>
      )}
      {isPublished && publishPlan.story && storyState === 'manual' && (
        <p className="ig-preview-story-hint muted" role="status">
          Story niet automatisch online — download en plaats in Instagram met link-sticker.
        </p>
      )}
      {isPublished && publishPlan.story && (storyState === 'failed' || storyState === 'missing') && (
        <p className="ig-preview-story-hint muted" role="status">
          Story niet geplaatst
          {post.instagram_story_publish_error ? `: ${post.instagram_story_publish_error}` : '.'}{' '}
          <button type="button" className="ig-preview-inline-link" disabled={busy} onClick={publishStoryRetry}>
            Opnieuw
          </button>
        </p>
      )}

      {!isPublished && (
        <div className="ig-preview-publish-plan">
          <span className="ig-preview-publish-plan-label">Plaatsen op:</span>
          <label className="marketing-ig-checkbox">
            <input
              type="checkbox"
              checked={publishPlan.feed}
              disabled={busy}
              onChange={(e) => {
                const feed = e.target.checked;
                const story = feed ? publishPlan.story : publishPlan.story || true;
                if (!feed && !story) return;
                savePublishPlan({ feed, story });
              }}
            />
            Feed
          </label>
          <label className="marketing-ig-checkbox">
            <input
              type="checkbox"
              checked={publishPlan.story}
              disabled={busy}
              onChange={(e) => {
                const story = e.target.checked;
                const feed = story ? publishPlan.feed : publishPlan.feed || true;
                if (!feed && !story) return;
                savePublishPlan({ feed, story });
              }}
            />
            Story
          </label>
          <span className="muted ig-preview-publish-plan-hint">{publishPlanShort(publishPlan)}</span>
        </div>
      )}

      <div className="ig-preview-image-tools">
        {post.has_product_gallery ? (
          <>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={busy || galleryLoading}
              onClick={() => (galleryOpen ? setGalleryOpen(false) : loadGallery())}
            >
              <FiImage /> {galleryOpen ? 'Gallery verbergen' : 'Foto uit shop'}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={autoSfeer}>
              Auto sfeer
            </button>
            {post.is_vbp_deal ? (
              publishPlan.story ? (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={busy}
                  onClick={renderStoryTile}
                  title="9:16 story: foto + gradient + code (alleen voor Story)"
                >
                  Story-tegel
                </button>
              ) : null
            ) : publishPlan.story ? (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={busy}
                onClick={renderStoryTile}
                title="9:16 story met sfeerfoto + productnaam"
              >
                Story-tegel
              </button>
            ) : null}
          </>
        ) : (
          <span className="muted ig-preview-no-gallery">
            Geen shop-product — plak een afbeelding-URL hieronder, of{' '}
            <Link to="/marketing">koppel een product in de kalender</Link> voor gallery / auto sfeer.
          </span>
        )}
        {savedHint && <span className="ig-preview-saved">{savedHint}</span>}
      </div>

      {!isPublished && (publishPlan.feed || publishPlan.story) && (
        <div className="ig-preview-image-url-block">
          <label className="ig-preview-label">
            Afbeelding-URL (https)
            <input
              className="form-input"
              type="url"
              value={imageUrl}
              disabled={busy}
              placeholder="https://www.4shine.nl/wp-content/uploads/…"
              onChange={(e) => setImageUrl(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={busy || !imageUrl.trim()}
            onClick={() => patchPost({ instagram_image_url: imageUrl.trim() })}
          >
            <FiSave /> Afbeelding opslaan
          </button>
        </div>
      )}

      {galleryOpen && gallery.length > 0 && (
        <div className="ig-preview-gallery" role="listbox" aria-label="Productfoto's op 4shine.nl">
          {gallery.map((img) => (
            <button
              key={img.gallery_index}
              type="button"
              role="option"
              aria-selected={img.selected || img.url === imageUrl}
              className={`ig-preview-gallery-thumb${img.selected || img.url === imageUrl ? ' selected' : ''}`}
              title={img.alt || `Foto ${img.gallery_index + 1}`}
              onClick={() => selectGalleryImage(img)}
            >
              <img src={img.url} alt="" loading="lazy" />
              <span>{img.gallery_index + 1}</span>
            </button>
          ))}
        </div>
      )}

      {publishPlan.feed && (
      <div className="ig-preview-edit-block">
        <label className="ig-preview-label">
          Caption (Instagram)
          <textarea
            className="form-input ig-preview-caption-input"
            rows={4}
            value={caption}
            disabled={busy}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Publieke tekst voor Instagram…"
          />
        </label>
        <div className="ig-preview-caption-actions">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={busy || aiGenerating}
            onClick={generateAiCaption}
          >
            <FiZap /> {aiGenerating ? 'AI…' : 'AI-caption'}
          </button>
          <button type="button" className="btn btn-primary btn-sm" disabled={busy || aiGenerating} onClick={saveCaption}>
            <FiSave /> Caption opslaan
          </button>
        </div>
        {aiGenerating && <p className="muted ig-preview-ai-loading">AI-tekst wordt gegenereerd…</p>}
        {isPublished && (
          <p className="muted ig-preview-published-note">
            Live op Instagram — wijzigingen gelden voor nieuwe posts / overzicht, niet voor de bestaande post.
          </p>
        )}
      </div>
      )}

      {!isPublished && (
        <div className="ig-preview-schedule">
          <label className="ig-preview-label-inline">
            Datum
            <input
              type="date"
              className="form-input"
              value={startsOn}
              disabled={busy}
              onChange={(e) => setStartsOn(e.target.value)}
            />
          </label>
          <label className="ig-preview-label-inline">
            Tijd
            <input
              type="time"
              className="form-input"
              value={publishTime}
              disabled={busy}
              onChange={(e) => setPublishTime(e.target.value)}
            />
          </label>
          <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={saveSchedule}>
            Planning opslaan
          </button>
          <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={publishNow}>
            Nu publiceren
          </button>
        </div>
      )}

      {!isPublished && post.preview_state === 'scheduled' && !igAutoEnabled && (
        <p className="marketing-ig-error ig-preview-error">
          Automatisch posten staat uit — deze post wordt niet vanzelf live. Zet “Automatisering aan” in Instagram →
          Instellingen, of klik <strong>Nu publiceren</strong>.
        </p>
      )}

      {publishPlan.feed && (
        <div className="ig-preview-caption ig-preview-caption-readonly">
          <strong>{handle}</strong>{' '}
          <span className="ig-preview-caption-text">{instagramPreviewCaption || '—'}</span>
        </div>
      )}

      {post.instagram_publish_error && !post.instagram_post_id && (
        <p className="marketing-ig-error ig-preview-error">{post.instagram_publish_error}</p>
      )}

      <footer className="ig-preview-footer">
        <Link to="/marketing" className="ig-preview-link">
          Meer in kalender →
        </Link>
        {post.product_naam && <span className="muted">{post.product_naam}</span>}
        <button
          type="button"
          className="btn btn-secondary btn-sm ig-preview-delete"
          disabled={busy}
          onClick={removePost}
          title="Verwijder uit overzicht (niet van Instagram zelf)"
        >
          <FiTrash2 /> Verwijderen
        </button>
      </footer>
    </article>
  );
}

export default function InstagramFeedPanel() {
  const [posts, setPosts] = useState([]);
  const [accountLabel, setAccountLabel] = useState('4shine');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('scheduled');
  const [showManualComposer, setShowManualComposer] = useState(false);
  const [igAutoEnabled, setIgAutoEnabled] = useState(false);
  const [defaultPublishTime, setDefaultPublishTime] = useState('10:00');
  const [igVbpDefaults, setIgVbpDefaults] = useState({
    vbpDagdealTarget: 'story',
    vbpDagknallerTarget: 'both',
    vbpStoryEnabled: true,
  });
  const [igStoryManualLink, setIgStoryManualLink] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [feed, cfg, autoCfg] = await Promise.all([
        api.get('/marketing/instagram/feed'),
        api.get('/marketing/instagram/config'),
        api.get('/marketing/instagram/auto-config'),
      ]);
      setPosts(feed.data?.posts || []);
      setAccountLabel(cfg.data?.label || '4shine');
      setIgAutoEnabled(Boolean(autoCfg.data?.enabled));
      setDefaultPublishTime(autoCfg.data?.default_publish_time || '10:00');
      setIgVbpDefaults({
        vbpDagdealTarget: autoCfg.data?.vbp_dagdeal_publish_target || 'story',
        vbpDagknallerTarget: autoCfg.data?.vbp_dagknaller_publish_target || 'both',
        vbpStoryEnabled: autoCfg.data?.vbp_story_enabled !== false,
      });
      setIgStoryManualLink(autoCfg.data?.instagram_story_manual_link !== false);
    } catch (e) {
      console.error(e);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onRefresh = () => load();
    window.addEventListener('marketing-ig-feed-refresh', onRefresh);
    return () => window.removeEventListener('marketing-ig-feed-refresh', onRefresh);
  }, [load]);

  const handlePostUpdated = (id, patch) => {
    setPosts((prev) =>
      prev.map((p) => {
        if (p.id !== id) return p;
        return { ...p, ...patch };
      })
    );
  };

  const handlePostDeleted = (id) => {
    setPosts((prev) => prev.filter((p) => p.id !== id));
    window.dispatchEvent(new Event('marketing-stats-refresh'));
  };

  const filtered = useMemo(() => {
    const list = posts.filter((p) => {
      if (filter === 'published') return p.preview_state === 'published';
      if (filter === 'scheduled') return p.preview_state === 'scheduled';
      if (filter === 'draft') return p.preview_state === 'draft';
      return true;
    });
    return [...list].sort((a, b) => compareInstagramPlanning(a, b, filter));
  }, [posts, filter]);

  return (
    <div className="marketing-ig-feed">
      {showManualComposer ? (
        <InstagramManualPostComposer
          defaultPublishTime={defaultPublishTime}
          igAutoEnabled={igAutoEnabled}
          onCreated={() => {
            setShowManualComposer(false);
            load();
          }}
          onClose={() => setShowManualComposer(false)}
        />
      ) : null}

      {!igAutoEnabled && (
        <p className="marketing-ig-auto-banner marketing-ig-error" role="status">
          Instagram-automatisering staat uit. Geplande posts worden pas online gezet als je in{' '}
          <Link to="/marketing/instagram">Instellingen</Link> “Automatisering aan” vinkt (controle elke 5 min), of per
          post op <strong>Nu publiceren</strong> klikt.
        </p>
      )}

      <div className="marketing-ig-feed-toolbar">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setShowManualComposer(true)}
          disabled={showManualComposer}
        >
          <FiPlus /> Nieuwe post
        </button>
        <div className="marketing-ig-feed-filters">
          {[
            ['all', 'Alles'],
            ['scheduled', 'Gepland'],
            ['published', 'Gepubliceerd'],
            ['draft', 'Concept'],
          ].map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={`btn btn-secondary ${filter === id ? 'active' : ''}`}
              onClick={() => setFilter(id)}
            >
              {label}
            </button>
          ))}
        </div>
        <button type="button" className="btn btn-secondary" onClick={load} disabled={loading}>
          <FiRefreshCw /> Vernieuwen
        </button>
      </div>

      {loading ? (
        <p className="muted">Laden…</p>
      ) : filtered.length === 0 ? (
        <p className="muted">
          Geen posts in dit overzicht. Plan campagnes in de{' '}
          <Link to="/marketing">kalender</Link>, voeg sfeerfoto toe en keur goed voor auto-post.
        </p>
      ) : (
        <div className="ig-preview-grid">
          {filtered.map((post) => (
            <InstagramPreviewCard
              key={post.id}
              post={post}
              accountLabel={accountLabel}
              defaultPublishTime={defaultPublishTime}
              igAutoEnabled={igAutoEnabled}
              igStoryManualLink={igStoryManualLink}
              igVbpDefaults={igVbpDefaults}
              onPostUpdated={handlePostUpdated}
              onPostDeleted={handlePostDeleted}
            />
          ))}
        </div>
      )}
    </div>
  );
}
